import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { Button, Input, Label, Modal, ModalHeader, ModalBody } from 'reactstrap'
import CsvDownloader from 'react-csv-downloader'
import Select from '../../../../components/Select'
import { getChaveDadosAoVivo } from '../../../../services/equipamentos'
import auth from '../../../../utils/auth'
import helper from '../../../../utils/helper'
import demoSession from '../../../../tours/demo/DemoSession'
import { mqttPacketPayload } from '../../../../tours/demo/mock/fixtures/mqttPacket'
import './mqtt-preview.css'

const userTimezone = auth.getUserInfo()?.timezone ?? undefined

const getPayload = (payload) => {
  if (!payload) return null
  const alvo = Array.isArray(payload) ? payload[0] : payload
  if (!alvo || typeof alvo !== 'object' || Array.isArray(alvo)) return null
  const resultado = {}
  const extrairChaves = (obj, prefixo = '') => {
    if (obj == null) return
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => extrairChaves(item, `${prefixo}${prefixo ? '.' : ''}${index}`))
      return
    }
    if (typeof obj === 'object') {
      Object.entries(obj).forEach(([chave, valor]) => {
        const novaChave = prefixo ? `${prefixo}.${chave}` : chave
        if (valor !== null && typeof valor === 'object') extrairChaves(valor, novaChave)
        else resultado[novaChave] = valor
      })
      return
    }
    resultado[prefixo] = obj
  }
  extrairChaves(alvo)
  return Object.keys(resultado).length ? resultado : null
}

const opcoesLimite = [
  { value: 5, label: '5 eventos' },
  { value: 10, label: '10 eventos' },
  { value: 20, label: '20 eventos' },
  { value: 50, label: '50 eventos' },
  { value: 100, label: '100 eventos' },
  { value: 200, label: '200 eventos' },
]

const formatarValor = (valor) => {
  if (valor == null) return '-'
  if (Array.isArray(valor)) return valor.join(', ')
  if (typeof valor === 'object') return JSON.stringify(valor)
  return String(valor)
}

const formatarHorario = (timestamp) => {
  if (!timestamp) return '-'

  const timezone = auth.getUserInfo()?.timezone ?? undefined

  return new Date(Number(timestamp) * 1000).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timezone,
  })
}

const formatarUltimaVez = (timestamp) => {
  if (!timestamp) return null

  const timezone = auth.getUserInfo()?.timezone ?? undefined

  return new Date(Number(timestamp) * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timezone,
  })
}

const calcularTendencia = (coluna, mensagens, indice) => {
  if (indice >= mensagens.length - 1) return null
  const payloadAtual = getPayload(mensagens[indice]?.payload)
  const payloadAnterior = getPayload(mensagens[indice + 1]?.payload)
  const valorAtual = payloadAtual?.[coluna]
  const valorAnterior = payloadAnterior?.[coluna]
  if (valorAtual == null || valorAnterior == null) return null
  const converterParaNumero = (v) => parseFloat(Array.isArray(v) ? v[0] : v)
  const atual = converterParaNumero(valorAtual)
  const anterior = converterParaNumero(valorAnterior)
  if (isNaN(atual) || isNaN(anterior)) return valorAtual !== valorAnterior ? 'diferente' : null
  if (atual === anterior) return null
  return atual > anterior ? 'subiu' : 'desceu'
}

const BadgeStatus = ({ pausado, conectado }) => {
  const getClasse = () => {
    if (!conectado) return 'mqtt-status-desconectado'
    if (pausado) return 'mqtt-status-pausado'
    return 'mqtt-status-conectado'
  }
  const getTexto = () => {
    if (!conectado) return 'Desconectado'
    if (pausado) return 'Pausado'
    return 'Conectado'
  }

  const handleInfoClick = (e) => {
    e.stopPropagation()
    helper.dispatchEvent('showAviso', {
      message: 'Esse status indica apenas que o sistema está conectado ao servidor WebSocket (canal de comunicação em tempo real). Isso não significa que o equipamento esteja enviando dados. Os dados do equipamento só aparecem na tabela abaixo quando ele envia informações.',
      callbackLabelClose: 'Fechar',
    })
  }

  return (
    <div className={`mqtt-badge ${getClasse()}`}>
      <span />
      {getTexto()}
      {!pausado && (
        <i
          className="las la-info-circle mqtt-badge-info"
          style={{ cursor: 'pointer', position: 'relative', top: '1px' }}
          title="O que significa esse status?"
          onClick={handleInfoClick}
        />
      )}
    </div>
  )
}

const BannerPendentes = ({ quantidade, aoMostrar }) => {
  if (!quantidade) return null
  return (
    <div className="mqtt-banner-pendentes">
      <span> {quantidade} novo(s) evento(s) em espera</span>
      <span className="mqtt-banner-link" onClick={aoMostrar}>
        Mostrar agora
      </span>
    </div>
  )
}

const RodapeTabela = ({ totalExibido, modoRaw, ultimaVez, nuncaComunicou }) => (
  <div className="mqtt-rodape">
    <span>
      {totalExibido} evento{totalExibido !== 1 ? 's' : ''} exibido{totalExibido !== 1 ? 's' : ''}
      {ultimaVez && (
        <span className="mqtt-rodape-ultima-vez" data-tour="mqtt-preview-ultima-vez"> · Visto por último em {ultimaVez}</span>
      )}
      {!ultimaVez && nuncaComunicou && (
        <span className="mqtt-rodape-ultima-vez" data-tour="mqtt-preview-ultima-vez"> · Aguardando primeira conexão</span>
      )}
    </span>

    {!modoRaw && !nuncaComunicou && (
      <span className="mqtt-rodape-legenda">
        <span>
          <span className="seta-diferente">≠</span> diferente
        </span>
        <span>
          <span className="seta-subiu">▲</span> aumentou
        </span>
        <span>
          <span className="seta-desceu">▼</span> diminuiu
        </span>
        <span className="mqtt-rodape-referencia">Comparado ao evento anterior</span>
      </span>
    )}
  </div>
)

const CelulaValor = ({ tendencia, valor }) => (
  <td className="mqtt-td-celula">
    {tendencia === 'diferente' && <span className="seta-diferente">≠ </span>}
    {tendencia === 'subiu' && <span className="seta-subiu">▲ </span>}
    {tendencia === 'desceu' && <span className="seta-desceu">▼ </span>}
    {valor}
  </td>
)

const ModalDetalhe = ({ mensagem, aoFechar }) => {
  if (!mensagem) return null
  const horario = formatarHorario(mensagem.timestamp)
  const jsonFormatado = JSON.stringify(mensagem, null, 2)
  return (
    <Modal isOpen toggle={aoFechar} size="lg">
      <ModalHeader toggle={aoFechar}>{horario}</ModalHeader>
      <ModalBody>
        <pre className="mqtt-modal-json">{jsonFormatado}</pre>
      </ModalBody>
    </Modal>
  )
}

export default function MqttPreview({ deviceId }) {
  const [mensagens, setMensagens] = useState([])
  const [conectado, setConectado] = useState(false)
  const [pausado, setPausado] = useState(false)
  const [pendentes, setPendentes] = useState([])
  const [limite, setLimite] = useState(5)
  const [mensagemAberta, setMensagemAberta] = useState(null)
  const [filtroTexto, setFiltroTexto] = useState('')
  const [ultimaVez, setUltimaVez] = useState(null)
  const [nuncaComunicou, setNuncaComunicou] = useState(true)

  const limiteRef = useRef(limite)
  const pausadoRef = useRef(pausado)
  const pendentesRef = useRef(pendentes)
  const ultimoHeartbeatRef = useRef(null)
  const heartbeatTimerRef = useRef(null)

  useEffect(() => { limiteRef.current = limite }, [limite])
  useEffect(() => {
    pausadoRef.current = pausado
    pendentesRef.current = pendentes
  }, [pausado, pendentes])

  useEffect(() => {
    setFiltroTexto('')
  }, [deviceId])

  useEffect(() => {
    if (!deviceId) return

    setMensagens([])
    setPendentes([])
    setPausado(false)
    setConectado(false)
    setUltimaVez(null)
    setNuncaComunicou(true)
    ultimoHeartbeatRef.current = null

    if (demoSession.isActive()) {
      const demoMessageTimestamp = mqttPacketPayload.timestamp || Math.floor(Date.now() / 1000)
      const demoLiveMessage = {
        type: 'message',
        timestamp: demoMessageTimestamp,
        payload: mqttPacketPayload,
      }

      setConectado(true)
      setNuncaComunicou(false)
      setUltimaVez(demoMessageTimestamp)
      setMensagens([demoLiveMessage])

      return () => {
        setConectado(false)
      }
    }

    let cancelado = false
    let socket = null

    getChaveDadosAoVivo().then((chave) => {
      if (cancelado || !chave) return

      const url = `${chave.websocket_base_url}${deviceId}?api_key=${chave.websocket_api_key}`
      socket = new WebSocket(url)

      socket.onclose = () => setConectado(false)
      socket.onerror = (erro) => { console.error(erro); setConectado(false) }

      socket.onmessage = ({ data }) => {
        try {
          const pacote = JSON.parse(data)

          if (pacote.type === 'connected') {
            setConectado(true)
            return
          }

          if (pacote.type === 'heartbeat') {
            ultimoHeartbeatRef.current = Date.now()
            return
          }
          if (pacote.type === 'history') {
            const historico = (pacote.messages ?? [])
              .filter((m) => m.type === 'message')
              .reverse()
              .map((m, i) => (i === 0 ? { ...m, type: 'history' } : m))

            if (historico.length > 0) {

              setUltimaVez(historico[0].timestamp)
              setNuncaComunicou(false)
              setMensagens((prev) => {
                const unificado = [...historico, ...prev]
                return unificado.length > limiteRef.current
                  ? unificado.slice(0, limiteRef.current)
                  : unificado
              })
            }
            return
          }

          if (pacote.type !== 'message') return

          setUltimaVez(pacote.timestamp)
          setNuncaComunicou(false)

          if (pausadoRef.current) {
            setPendentes((prev) => [...prev, pacote])
          } else {
            setMensagens((prev) => {
              const atualizado = [pacote, ...prev]
              return atualizado.length > limiteRef.current
                ? atualizado.slice(0, limiteRef.current)
                : atualizado
            })
          }
        } catch (erro) {
          console.error(erro)
        }
      }

      heartbeatTimerRef.current = setInterval(() => {
        if (ultimoHeartbeatRef.current && Date.now() - ultimoHeartbeatRef.current > 60_000) {
          setConectado(false)
        }
      }, 10_000)
    })

    return () => {
      cancelado = true
      setConectado(false)
      if (socket) socket.close()
      clearInterval(heartbeatTimerRef.current)
    }
  }, [deviceId])

  const modoRaw = mensagens.length > 0 && mensagens.every((m) => !getPayload(m.payload))

  const mensagensExibidas = useMemo(() => mensagens, [mensagens])

  const colunas = useMemo(() => {
    const chaves = new Set()
    mensagensExibidas.forEach(({ payload }) => {
      const payloadNormalizado = getPayload(payload)
      if (payloadNormalizado) Object.keys(payloadNormalizado).forEach((k) => chaves.add(k))
    })
    return [...chaves]
  }, [mensagensExibidas])

  const colunasFiltradas = useMemo(() => {
    if (!filtroTexto.trim() || modoRaw) return colunas
    const termo = filtroTexto.toLowerCase().trim()
    return colunas.filter((col) => {
      if (col.toLowerCase().includes(termo)) return true
      return mensagensExibidas.some((msg) => {
        const v = getPayload(msg.payload)?.[col]
        return formatarValor(v).toLowerCase().includes(termo)
      })
    })
  }, [colunas, filtroTexto, modoRaw, mensagensExibidas])

  const mensagensFiltradas = useMemo(() => {
    if (!filtroTexto.trim()) return mensagensExibidas
    const termo = filtroTexto.toLowerCase().trim()
    return mensagensExibidas.filter((msg) => {
      if (!getPayload(msg.payload)) {
        return JSON.stringify(msg.payload ?? msg).toLowerCase().includes(termo)
      }
      const payloadNormalizado = getPayload(msg.payload) || {}
      return colunasFiltradas.some((col) => {
        return (
          col.toLowerCase().includes(termo) ||
          formatarValor(payloadNormalizado[col]).toLowerCase().includes(termo)
        )
      })
    })
  }, [mensagensExibidas, filtroTexto, colunasFiltradas])

  const dadosCSV = useMemo(() => {
    if (!mensagensExibidas.length) return { colunas: [], linhas: [] }

    if (modoRaw) {
      return {
        colunas: [
          { id: 'horario', displayName: 'horario' },
          { id: 'pacote', displayName: 'pacote' },
        ],
        linhas: mensagensExibidas.map((msg) => ({
          horario: formatarHorario(msg.timestamp),
          pacote: JSON.stringify(msg.payload ?? msg),
        })),
      }
    }

    const temRaw = mensagensExibidas.some((m) => !getPayload(m.payload))
    const colunasBase = [
      { id: 'horario', displayName: 'horario' },
      ...colunas.map((col) => ({ id: col, displayName: col })),
      ...(temRaw ? [{ id: 'pacote', displayName: 'pacote' }] : []),
    ]

    const linhas = mensagensExibidas.map((msg) => {
      const horario = formatarHorario(msg.timestamp)
      const payload = getPayload(msg.payload)

      if (payload) {
        return {
          horario,
          ...Object.fromEntries(
            colunas.map((col) => {
              const v = payload[col]
              if (v == null) return [col, '']
              if (Array.isArray(v)) return [col, v.join('|')]
              return [col, String(v)]
            })
          ),
          ...(temRaw ? { pacote: '' } : {}),
        }
      }

      return {
        horario,
        ...Object.fromEntries(colunas.map((col) => [col, ''])),
        ...(temRaw
          ? {
            pacote:
              typeof msg.payload === 'string'
                ? msg.payload
                : JSON.stringify(msg.payload ?? msg),
          }
          : {}),
      }
    })

    return { colunas: colunasBase, linhas }
  }, [mensagensExibidas, colunas, modoRaw])

  const mostrarPendentes = useCallback(() => {
    setMensagens((prev) => {
      const unificado = [...pendentesRef.current, ...prev]
      return unificado.length > limite ? unificado.slice(0, limite) : unificado
    })
    setPendentes([])
    setPausado(false)
  }, [limite])

  const handlePausar = useCallback(() => {
    pausado ? mostrarPendentes() : setPausado(true)
  }, [pausado, mostrarPendentes])

  const handleLimite = useCallback((option) => {
    const novoLimite = option.value
    setLimite(novoLimite)
    setMensagens((prev) => (prev.length > novoLimite ? prev.slice(0, novoLimite) : prev))
  }, [])

  const handleLimpar = useCallback(() => {
    setMensagens([])
    setPendentes([])
    setPausado(false)
    setFiltroTexto('')
  }, [])

  const handleExportar = useCallback(() => {
    exportarCSV(mensagensExibidas, colunas, modoRaw)
  }, [mensagensExibidas, colunas, modoRaw])

  const handleAbrirLinha = useCallback((msg) => setMensagemAberta(msg), [])
  const handleFecharModal = useCallback(() => setMensagemAberta(null), [])

  return (
    <div className="mqtt-container" data-tour="mqtt-preview">
      <div className="mqtt-cabecalho">
        <div className="mqtt-toolbar-esquerda">
          <div className="mqtt-input-wrapper">
            <i className="fa fa-search" />
            <Input
              placeholder="Filtrar campos..."
              bsSize="sm"
              className="mqtt-input-search"
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
            />
          </div>
        </div>

        <div className="mqtt-badge-container">
          <BadgeStatus pausado={pausado} conectado={conectado} />
        </div>

        <div className="mqtt-toolbar-direita">
          <div className="mqtt-controle-limite">
            <Label className="mqtt-label-limite">Exibir</Label>
            <Select
              className="mqtt-select-limite"
              value={opcoesLimite.find((item) => item.value === limite)}
              onChange={handleLimite}
              options={opcoesLimite}
              isSearchable={false}
            />
          </div>
          <Button color={pausado ? 'success' : 'warning'} onClick={handlePausar}>
            {pausado ? 'Retomar' : 'Pausar'}
          </Button>
          <CsvDownloader
            filename={`mqtt_eventos_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}`}
            extension=".csv"
            separator=","
            columns={dadosCSV.colunas}
            datas={dadosCSV.linhas}
            disabled={!mensagensExibidas.length}
          >
            <Button color="primary" disabled={!mensagensExibidas.length}>
              Exportar CSV
            </Button>
          </CsvDownloader>
          <Button color="danger" onClick={handleLimpar}>
            Limpar
          </Button>
        </div>
      </div>

      {pausado && <BannerPendentes quantidade={pendentes.length} aoMostrar={mostrarPendentes} />}

      <div className="mqtt-tabela-scroll-vertical">
        <div className="mqtt-tabela-scroll-horizontal">
          <table className="table table-sm table-bordered table-hover afira-table mqtt-tabela">
            {mensagensExibidas.length > 0 && (
              <thead>
                <tr>
                  <th className="mqtt-th-horario">Horário</th>
                  {modoRaw ? (
                    <th>Pacote</th>
                  ) : (
                    <>
                      {colunasFiltradas.map((col) => (
                        <th key={col} className="mqtt-th-coluna">{col}</th>
                      ))}
                      {mensagens.some((m) => !getPayload(m.payload)) && (
                        <th className="mqtt-th-coluna">Pacote</th>
                      )}
                    </>
                  )}
                </tr>
              </thead>
            )}
            <tbody>
              {mensagensExibidas.length === 0 ? (
                <tr>
                  <td className="mqtt-linha-vazia">Aguardando mensagens...</td>
                </tr>
              ) : (
                mensagensFiltradas.map((msg, indice) => {
                  const horario = formatarHorario(msg.timestamp)
                  return (
                    <tr
                      key={indice}
                      className="mqtt-linha-clicavel"
                      onClick={() => handleAbrirLinha(msg)}
                    >
                      <td className="mqtt-td-horario">{horario}</td>
                      {getPayload(msg.payload) ? (
                        <>
                          {colunasFiltradas.map((col) => (
                            <CelulaValor
                              key={col}
                              valor={formatarValor(getPayload(msg.payload)?.[col])}
                              tendencia={calcularTendencia(col, mensagensExibidas, indice)}
                            />
                          ))}
                          {mensagens.some((m) => !getPayload(m.payload)) && (
                            <td className="mqtt-td-celula">-</td>
                          )}
                        </>
                      ) : (
                        <>
                          {colunasFiltradas.map((col) => (
                            <td key={col} className="mqtt-td-celula">-</td>
                          ))}
                          <td className="mqtt-td-raw">
                            {typeof msg.payload === 'string'
                              ? msg.payload
                              : JSON.stringify(msg.payload ?? msg)}
                          </td>
                        </>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RodapeTabela
        totalExibido={mensagensExibidas.length}
        modoRaw={modoRaw}
        ultimaVez={ultimaVez ? formatarUltimaVez(ultimaVez) : null}
        nuncaComunicou={nuncaComunicou}
      />

      <ModalDetalhe mensagem={mensagemAberta} aoFechar={handleFecharModal} />
    </div>
  )
}